import { useCallback, useEffect, useRef } from "react";
import { roomApi } from "./roomApi";

// Operations are batched over a frame or two rather than sent one per change:
// dragging a note produces a change per pointer move, and they coalesce into
// one request without anyone noticing the difference.
const FLUSH_MS = 120;

// A stroke in progress is broadcast at about 25 a second. Fast enough to look
// continuous, slow enough that a long stroke is tens of requests, not hundreds.
const LIVE_MS = 40;

/**
 * Sends what changed on the board, rather than the board.
 *
 * Two channels, deliberately:
 *
 *   ops   durable — a note added, moved, deleted; a finished stroke. Batched,
 *         stored, and broadcast to everyone watching
 *   live  ephemeral — the points of a stroke as it is being drawn. Broadcast
 *         and thrown away, so a student sees the line appear rather than
 *         waiting for the pen to lift
 *
 * The live channel is allowed to lose things. Whatever it drops, the durable
 * operation at the end of the stroke puts right.
 */
export function useBoardOps({ sessionGuid, subRoomGuid = null, enabled = true }) {
  const queue = useRef([]);
  const timer = useRef(null);
  const lastLive = useRef(0);
  const live = Boolean(sessionGuid) && enabled;

  const flush = useCallback(async () => {
    timer.current = null;
    const ops = queue.current;
    if (!ops.length || !live) return;
    queue.current = [];
    try {
      await roomApi.sendBoardOps(sessionGuid, ops, subRoomGuid);
    } catch (e) {
      // Put them back: the next change will carry them, and a board that
      // silently dropped an operation would diverge from every other screen.
      queue.current = [...ops, ...queue.current];
      console.error("Board operations failed to send", e);
    }
  }, [live, sessionGuid, subRoomGuid]);

  const emit = useCallback(
    (op) => {
      if (!live) return;
      queue.current.push(op);
      if (!timer.current) timer.current = setTimeout(flush, FLUSH_MS);
    },
    [live, flush]
  );

  /** Throttled: a pointer can move far more often than anyone can see. */
  const emitLive = useCallback(
    (type, payload) => {
      if (!live) return;
      const now = Date.now();
      if (now - lastLive.current < LIVE_MS) return;
      lastLive.current = now;
      roomApi.sendBoardLive(sessionGuid, type, payload, subRoomGuid);
    },
    [live, sessionGuid, subRoomGuid]
  );

  // Anything queued when the room closes — or when the board changes to
  // another room's — still has to go, and to the board it was made on.
  useEffect(
    () => () => {
      clearTimeout(timer.current);
      timer.current = null;
      if (queue.current.length && live) {
        roomApi.sendBoardOps(sessionGuid, queue.current, subRoomGuid).catch(() => {});
      }
      queue.current = [];
    },
    [live, sessionGuid, subRoomGuid]
  );

  return { emit, emitLive, isLive: live };
}
